<template>
  <div :class="scope.clsPrefix">
    <div class="header-left">
      <div class="main-title">{{ title }}</div>
      <!-- 企业下拉选择器 -->
      <el-select
        v-model="currentEnterpriseNo"
        class="enterprise-select"
        popper-class="enterprise-select-popper"
        :loading="switchingEnterprise || loadingEnterprise"
        placeholder="请选择企业"
        size="small"
        filterable
      >
        <el-option
          v-for="ent in enterpriseList"
          :key="ent.no"
          :label="ent.name"
          :value="ent.no"
        >
          <span class="ent-option-label">
            <span class="ent-option-name">{{ ent.name }}</span>
            <span v-if="ent.no === lastEnterpriseNo" class="ent-option-current">当前</span>
          </span>
        </el-option>
      </el-select>
      <div class="chat-btn" @click="openDrawer">
        <i class="el-icon-chat-dot-square"></i>
        <span>立即对话</span>
      </div>
      <!-- <div class="sub-title">生产经营协同中枢</div> -->
    </div>
    <div class="header-right">
      <span class="datetime">{{ currentDateTime }}</span>
      <span class="time">{{ currentTime }}</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { getToken } from '@/utils/auth'

export default {
  name: 'HeaderCard',
  props: {
    // 工作台标题：默认 CEO 工作台，其他数字人（如能源站长）可通过 prop 覆盖
    title: {
      type: String,
      default: '能源站长智擎工作台'
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-header-card'
      },
      currentDateTime: '',
      currentTime: '',
      isDrawerMode: window.innerWidth < 1750,
      // 企业切换相关
      enterpriseList: [],
      // 下拉框绑定值（v-model 会在用户点击选项时同步更新）
      currentEnterpriseNo: '',
      // 真正生效的当前企业（只有接口成功后才更新），用于比对新旧值
      lastEnterpriseNo: '',
      loadingEnterprise: false,
      switchingEnterprise: false
    }
  },
  watch: {
    /**
     * 监听下拉框值变化（v-model 先于 @change 触发更新）
     * 只有新值与 lastEnterpriseNo（真正生效的企业）不同时才发起切换请求
     */
    currentEnterpriseNo(newVal) {
      if (!newVal) return
      if (newVal === this.lastEnterpriseNo) return
      this.handleSwitchEnterprise(newVal)
    }
  },
  created() {
    this.updateDateTime()
    this.timer = setInterval(() => {
      this.updateDateTime()
    }, 1000)
    window.addEventListener('resize', this._checkDrawerMode)
  },
  mounted() {
    this.loadEnterprises()
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
    window.removeEventListener('resize', this._checkDrawerMode)
  },
  methods: {
    _checkDrawerMode() {
      this.isDrawerMode = window.innerWidth < 1750
    },
    openDrawer() {
      if (typeof window.openDrawer === 'function') {
        window.openDrawer()
      }
    },
    updateDateTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
      const weekDay = weekDays[now.getDay()]
      this.currentDateTime = `${year}-${month}-${day}     ${weekDay}`
      this.currentTime = `${hours}:${minutes}:${seconds}`
    },
    /**
     * 加载企业列表：GET /energy2-api/uac/user/enterprises
     * 返回结构：{ code: 200, data: [ { no, name, isCurrentEnt, ... } ] }
     */
    async loadEnterprises() {
      this.loadingEnterprise = true
      try {
        const response = await axios.get('/energy2-api/uac/user/enterprises', {
          headers: { token: getToken() }
        })
        const res = response.data || {}
        if (res.code !== 200) {
          throw new Error(res.msg || res.message || '获取企业列表失败')
        }
        const list = Array.isArray(res.data) ? res.data : []
        this.enterpriseList = list
        // 找到当前企业（isCurrentEnt === 1）
        const current = list.find(e => e && e.isCurrentEnt === 1)
        const no = current ? current.no : (list.length > 0 ? list[0].no : '')
        if (no) {
          // 初始化时同时更新 v-model 值和真正生效值
          this.currentEnterpriseNo = no
          this.lastEnterpriseNo = no
        }
      } catch (e) {
        console.error('获取企业列表失败:', e)
        // 静默失败，不影响其他功能
      } finally {
        this.loadingEnterprise = false
      }
    },
    /**
     * 切换企业：PUT /energy2-api/uac/user/{no}/rel
     * @param {string} newNo 目标企业编号
     */
    async handleSwitchEnterprise(newNo) {
      this.switchingEnterprise = true
      const prevNo = this.lastEnterpriseNo
      try {
        const response = await axios.put(`/energy2-api/uac/user/${newNo}/rel`, {}, {
          headers: { token: getToken() }
        })
        const res = response.data || {}
        if (res.code !== 200) {
          throw new Error(res.msg || res.message || '切换企业失败')
        }
        // 切换成功：更新真正生效的企业编号
        this.lastEnterpriseNo = newNo
        // 通知父组件刷新子组件数据
        this.$emit('enterprise-switched', newNo)
        // 重新加载企业列表以刷新 isCurrentEnt 标识
        await this.loadEnterprises()
      } catch (e) {
        console.error('切换企业失败:', e)
        // 失败回滚：v-model 恢复到真正生效的企业
        this.currentEnterpriseNo = prevNo
        this.$message && this.$message.error('切换企业失败，请稍后重试')
      } finally {
        this.switchingEnterprise = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-header-card";
.#{$class-prefix} {
  display: flex;
  // align-items: center;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 0px;
  padding-top: 0;
  // background: linear-gradient(135deg, #4A5FF3 0%, #6B7EFF 100%);
  border-radius: 8px;
  box-sizing: border-box;
}
.header-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
}
.main-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  font-family: PingFangSC-Bold;
}

/* 企业下拉选择器 */
.enterprise-select {
  width: 240px;
}
.enterprise-select ::v-deep .el-input__inner {
  border: 1px solid #4a5ff3;
  border-radius: 4px;
  color: #4a5ff3;
  font-size: 12px;
  height: 28px;
  line-height: 26px;
  background-color: #fff;
  padding-right: 26px;
}
.enterprise-select ::v-deep .el-input__inner::placeholder {
  color: #aab3ff;
}
.enterprise-select ::v-deep .el-input.is-focus .el-input__inner {
  border-color: #4a5ff3;
}
.enterprise-select ::v-deep .el-input__suffix {
  color: #4a5ff3;
  font-size: 12px;
}
.enterprise-select ::v-deep .el-input__clear {
  color: #4a5ff3;
}
.enterprise-select ::v-deep .el-select .el-input .el-input__inner {
  padding-right: 30px;
}

.chat-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 9px;
  padding: 5px 16px;
  border: 1px solid #4a5ff3;
  border-radius: 4px;
  font-size: 12px;
  color: #4a5ff3;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.chat-btn i {
  font-size: 12px;
}
.sub-title {
  font-size: 14px;
  color: #595959;
  font-family: PingFangSC-Regular;
}
.header-right {
  display: flex;
  align-items: center;
}
.datetime {
  font-size: 14px;
  color: #595959;
  font-family: PingFangSC-Regular;
  letter-spacing: 1px;
}
.time {
  font-size: 16px;
  color: #333;
  font-family: PingFangSC-Regular;
  // letter-spacing: 1px;
  font-weight: 600;
  margin-left: 16px;
}
</style>

<style lang="scss">
/* 下拉弹层样式（非 scoped） */
.enterprise-select-popper {
  .ent-option-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  .ent-option-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ent-option-current {
    margin-left: 12px;
    font-size: 11px;
    color: #4a5ff3;
    flex-shrink: 0;
  }
}
</style>
